import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";

const OTPInput = ({
  value,
  onChange,
  onComplete,
  label,
  required,
  disabled,
  error,
  autoFocus,
  length,
}) => {
  const inputRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  // Handle input change for individual cells
  const handleInputChange = (index, inputValue) => {
    // Only allow single digits
    if (inputValue && !/^\d$/.test(inputValue)) return;

    const newValue = [...value];
    newValue[index] = inputValue;
    onChange(newValue);

    // Auto-advance to next input if digit entered
    if (inputValue && index < length - 1) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }

    // Auto-complete when all digits are filled
    if (
      inputValue &&
      index === length - 1 &&
      newValue.every((digit) => digit !== "")
    ) {
      const fullCode = newValue.join("");
      if (onComplete) {
        onComplete(fullCode);
      }
    }
  };

  // Handle backspace and arrow key navigation
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !value[index] && index > 0) {
      const newValue = [...value];
      newValue[index - 1] = "";
      onChange(newValue);
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    } else if (e.key === "ArrowRight" && index < length - 1) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  // Handle paste functionality
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain");
    const digits = pastedData.replace(/\D/g, "").split("").slice(0, length);

    if (digits.length > 0) {
      const newValue = [...value];
      digits.forEach((digit, index) => {
        if (index < length) {
          newValue[index] = digit;
        }
      });
      onChange(newValue);

      // Focus the next empty input or the last input
      const nextEmptyIndex = newValue.findIndex((digit) => digit === "");
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : length - 1;
      if (inputRefs.current[focusIndex]) {
        inputRefs.current[focusIndex].focus();
      }

      // Auto-complete if all digits are filled
      if (newValue.every((digit) => digit !== "")) {
        const fullCode = newValue.join("");
        if (onComplete) {
          onComplete(fullCode);
        }
      }
    }
  };

  return (
    <>
      {label && <label className={required ? "required" : ""}>{label}</label>}
      <div className="d-flex gap-1">
        {value.map((digit, index) => {
          const inputKey = `otp-input-${index}`;
          return (
            <input
              key={inputKey}
              ref={(el) => {
                inputRefs.current[index] = el;
              }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleInputChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoFocus={autoFocus && index === 0}
              disabled={disabled}
              className={error ? "error" : ""}
              placeholder=""
              aria-label={`${label || "OTP"} digit ${index + 1}`}
              style={{minWidth: 'auto', textAlign: 'center'}}
            />
          );
        })}
      </div>
    </>
  );
};

OTPInput.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  autoFocus: PropTypes.bool,
  length: PropTypes.number,
};

OTPInput.defaultProps = {
  onComplete: null,
  label: null,
  required: false,
  disabled: false,
  error: false,
  autoFocus: false,
  length: 6,
};

export default OTPInput;
